import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Page, Card, Button, Banner, Spinner, BlockStack, Text, InlineStack } from '@shopify/polaris';
import { useAppBridge } from '@shopify/app-bridge-react';
import { Redirect } from '@shopify/app-bridge/actions';
import { API_URL } from '../config/api';

type SessionStatus = 'checking' | 'valid' | 'missing' | 'error';

export default function Install() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const appBridge = useAppBridge();
  const shop = searchParams.get('shop') || '';
  const host = searchParams.get('host') || '';

  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('checking');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Check if session exists
  useEffect(() => {
    if (!shop) {
      setSessionStatus('error');
      setErrorMessage('Missing shop parameter');
      return;
    }

    const checkSession = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/check?shop=${encodeURIComponent(shop)}`);
        const data = await response.json();

        if (data.hasSession) {
          setSessionStatus('valid');
          // Redirect to dashboard after a short delay
          setTimeout(() => {
            navigate(`/?shop=${encodeURIComponent(shop)}${host ? `&host=${encodeURIComponent(host)}` : ''}`);
          }, 1500);
        } else {
          setSessionStatus('missing');
          setErrorMessage(data.message || 'Session not found');
        }
      } catch (error) {
        console.error('Session check failed:', error);
        setSessionStatus('missing');
        setErrorMessage('Could not verify session status');
      }
    };

    checkSession();
  }, [shop, host, navigate]);

  const handleInstall = () => {
    if (!shop || isRedirecting) {
      return;
    }

    setIsRedirecting(true);

    const authUrlParams = new URLSearchParams();
    authUrlParams.set('shop', shop);
    if (host) authUrlParams.set('host', host);

    const authUrl = `${API_URL}/api/auth?${authUrlParams.toString()}`;

    console.log('Redirecting to OAuth:', authUrl);

    // Use App Bridge redirect to break out of iframe
    if (appBridge) {
      try {
        const redirect = Redirect.create(appBridge);
        redirect.dispatch(Redirect.Action.REMOTE, {
          url: authUrl,
          newContext: true,
        });
      } catch (error) {
        console.error('App Bridge redirect failed:', error);
        // Fallback
        if (window.top && window.top !== window) {
          window.top.location.href = authUrl;
        } else {
          window.location.href = authUrl;
        }
      }
    } else {
      // Fallback if App Bridge not available
      if (window.top && window.top !== window) {
        window.top.location.href = authUrl;
      } else {
        window.location.href = authUrl;
      }
    }
  };

  // Auto-redirect to OAuth after checking session status
  useEffect(() => {
    if (sessionStatus === 'missing' && shop && !isRedirecting) {
      const timer = setTimeout(() => {
        handleInstall();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [sessionStatus, shop, isRedirecting]);

  if (!shop) {
    return (
      <Page title="Install App">
        <Card>
          <BlockStack gap="400">
            <Banner tone="critical" title="Missing Shop Parameter">
              <Text as="p">The shop parameter is required to install the app.</Text>
            </Banner>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  if (sessionStatus === 'checking') {
    return (
      <Page title="Product Labels App">
        <Card>
          <BlockStack gap="400">
            <InlineStack gap="300" align="center">
              <Spinner size="small" />
              <Text as="p" variant="bodyMd">
                Checking session status for {shop}...
              </Text>
            </InlineStack>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  if (sessionStatus === 'valid') {
    return (
      <Page title="Product Labels App">
        <Card>
          <BlockStack gap="400">
            <Banner tone="success" title="Session Valid">
              <Text as="p">
                Your session is valid. Redirecting to dashboard...
              </Text>
            </Banner>
            <InlineStack gap="300" align="center">
              <Spinner size="small" />
              <Text as="p" variant="bodyMd" tone="subdued">
                Redirecting...
              </Text>
            </InlineStack>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Install Product Labels App">
      <Card>
        <BlockStack gap="400">
          <Banner tone="warning" title="Authentication Required">
            <Text as="p">
              {errorMessage || 'This app needs to be installed and authorized to access your Shopify store data.'}
            </Text>
          </Banner>

          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              <strong>Shop:</strong> {shop}
            </Text>
          </BlockStack>

          {isRedirecting ? (
            <BlockStack gap="300">
              <InlineStack gap="300" align="center">
                <Spinner size="small" />
                <Text as="p" variant="bodyMd" tone="subdued">
                  Redirecting to Shopify OAuth...
                </Text>
              </InlineStack>
            </BlockStack>
          ) : (
            <BlockStack gap="300">
              <Text as="p" variant="bodyMd" tone="subdued">
                Click the button below or wait to be redirected automatically.
              </Text>
              <Button variant="primary" onClick={handleInstall}>
                Authorize App
              </Button>
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    </Page>
  );
}
